import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";

const avg = a => a?.length ? +(a.reduce((x,y)=>x+y,0)/a.length).toFixed(1) : 0;

const Badge = ({ label, color }) => (
  <span style={{ background:color+"22", color, fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20, border:`1px solid ${color}44` }}>
    {label}
  </span>
);

const StatCard = ({ value, label, color="#f97316", sub }) => (
  <div style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:"16px 14px", transition:"border-color .2s, box-shadow .2s" }}
    onMouseEnter={e => { e.currentTarget.style.borderColor="#f9731644"; e.currentTarget.style.boxShadow="0 0 16px rgba(249,115,22,0.1)"; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor="#1e293b"; e.currentTarget.style.boxShadow="none"; }}
  >
    <div style={{ fontSize:22, fontWeight:800, color }}>{value}</div>
    <div style={{ fontSize:12, color:"#64748b", marginTop:3 }}>{label}</div>
    {sub && <div style={{ fontSize:11, color:"#334155", marginTop:2 }}>{sub}</div>}
  </div>
);

const statusColor = { new:"#f97316", contacted:"#0891b2", converted:"#22c55e" };
const statusLabel = { new:"New", contacted:"Contacted", converted:"Converted" };

export default function AdminPanel() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [teachers,    setTeachers]    = useState([]);
  const [payments,    setPayments]    = useState([]);
  const [stats,       setStats]       = useState({});
  const [leads,         setLeads]         = useState([]);
  const [teacherLeads,  setTeacherLeads]  = useState([]);
  const [students,      setStudents]      = useState([]);
  const [settings,    setSettings]    = useState({ teacher_free_listings: 0, student_free_contacts: 0, credit_pack_price: 29, credits_per_pack: 3 });
  const [settingsSaved,      setSettingsSaved]      = useState(false);
  const [settingsSaveError,  setSettingsSaveError]  = useState("");
  const [tab,         setTab]         = useState("overview");
  const [loading,     setLoading]     = useState(true);
  const [tabLoading,  setTabLoading]  = useState(false);
  const [search,      setSearch]      = useState("");
  const [loaded,      setLoaded]      = useState({ teachers: false, students: false, teacherLeads: false });

  // On mount: load only Overview data (4 calls instead of 7)
  useEffect(() => {
    Promise.allSettled([
      api.get("/admin/stats"),
      api.get("/admin/payments"),
      api.get("/admin/leads"),
      api.get("/admin/settings"),
    ]).then(([s, p, l, st]) => {
      if (s.status === "fulfilled") setStats(s.value.data);
      if (p.status === "fulfilled") setPayments(p.value.data);
      if (l.status === "fulfilled") setLeads(Array.isArray(l.value.data) ? l.value.data : (l.value.data?.leads || []));
      if (st.status === "fulfilled") setSettings(st.value.data);
    }).finally(() => setLoading(false));
  }, []);

  // Lazy-load tab data on first visit
  useEffect(() => {
    if ((tab === "teachers" || tab === "analytics") && !loaded.teachers) {
      setTabLoading(true);
      api.get("/admin/teachers")
        .then(r => { setTeachers(r.data?.teachers || r.data || []); setLoaded(ld => ({ ...ld, teachers: true })); })
        .catch(() => {})
        .finally(() => setTabLoading(false));
    }
    if (tab === "students" && !loaded.students) {
      setTabLoading(true);
      api.get("/admin/students")
        .then(r => { setStudents(r.data.students ?? r.data); setLoaded(ld => ({ ...ld, students: true })); })
        .catch(() => {})
        .finally(() => setTabLoading(false));
    }
    if (tab === "teacher-leads" && !loaded.teacherLeads) {
      setTabLoading(true);
      api.get("/admin/teacher-leads")
        .then(r => { setTeacherLeads(r.data?.leads || r.data || []); setLoaded(ld => ({ ...ld, teacherLeads: true })); })
        .catch(() => {})
        .finally(() => setTabLoading(false));
    }
  }, [tab]);

  // Auto-poll teachers every 30s while on Teachers tab
  useEffect(() => {
    if (tab !== "teachers") return;
    const interval = setInterval(() => {
      api.get("/admin/teachers").then(r => setTeachers(r.data?.teachers || r.data || []));
    }, 30000);
    return () => clearInterval(interval);
  }, [tab]);

  const refreshTeachers = useCallback(() => {
    setTabLoading(true);
    api.get("/admin/teachers")
      .then(r => setTeachers(r.data?.teachers || r.data || []))
      .catch(() => {})
      .finally(() => setTabLoading(false));
  }, []);

  const verify = async id => {
    try {
      const { data } = await api.patch(`/admin/teachers/${id}/verify`);
      setTeachers(ts => ts.map(t => t._id===id ? {...t, verified:data.verified} : t));
    } catch { alert("Failed to toggle verification. Try again."); }
  };

  const block = async (id, currentlyBlocked) => {
    const action = currentlyBlocked ? "Unblock" : "Block";
    if (!window.confirm(`${action} this teacher?`)) return;
    try {
      const { data } = await api.patch(`/admin/teachers/${id}/block`);
      setTeachers(ts => ts.map(t => t._id===id ? {...t, blocked:data.blocked} : t));
    } catch { alert("Failed to update block status. Try again."); }
  };

  const activateListing = async id => {
    try {
      const { data } = await api.patch(`/admin/teachers/${id}/activate-listing`);
      setTeachers(ts => ts.map(t => t._id===id ? {...t, listing_active:data.listing_active, demo_awaiting_payment:data.demo_awaiting_payment} : t));
      setStats(s => ({ ...s, listedTeachers:(s.listedTeachers||0)+1 }));
    } catch { alert("Failed to activate listing. Try again."); }
  };

  const deactivateListing = async id => {
    if (!window.confirm("Deactivate this teacher's listing? They will be removed from search.")) return;
    try {
      const { data } = await api.patch(`/admin/teachers/${id}/deactivate-listing`);
      setTeachers(ts => ts.map(t => t._id===id ? {...t, listing_active:data.listing_active} : t));
    } catch { alert("Failed to deactivate listing. Try again."); }
  };

  const relistFree = async id => {
    if (!window.confirm("Relist this teacher for FREE? They will appear in search without paying the platform fee.")) return;
    try {
      const { data } = await api.patch(`/admin/teachers/${id}/reactivate-demo`);
      setTeachers(ts => ts.map(t => t._id===id ? {...t, listing_active:data.listing_active, demo_awaiting_payment:data.demo_awaiting_payment} : t));
    } catch { alert("Failed to relist teacher. Try again."); }
  };

  const updateLeadStatus = async (id, status) => {
    const { data } = await api.patch(`/admin/leads/${id}/status`, { status });
    setLeads(ls => ls.map(l => l._id===id ? { ...l, status:data.status } : l));
  };

  const filteredTeachers = teachers.filter(t =>
    search === "" ||
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.email?.toLowerCase().includes(search.toLowerCase()) ||
    t.city?.toLowerCase().includes(search.toLowerCase())
  );

  const saveSettings = async () => {
    setSettingsSaveError("");
    try {
      await api.put("/admin/settings", settings);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    } catch {
      setSettingsSaveError("✗ Save failed. Try again.");
    }
  };

  const adjustCredits = async (id, newCredits) => {
    const { data } = await api.patch(`/admin/students/${id}/credits`, { credits: newCredits });
    setStudents(ss => ss.map(s => s._id === id ? { ...s, credits: data.credits } : s));
  };

  const adjustTeacherCredits = async (id, newCredits) => {
    await api.patch(`/admin/teachers/${id}/credits`, { credits: newCredits });
    setTeachers(ts => ts.map(t => t._id === id ? { ...t, credits: newCredits } : t));
  };

  const forceUnlockLead = async (id) => {
    if (!window.confirm("Force-unlock this lead for FREE? Teacher will see student contact without paying.")) return;
    try {
      const { data } = await api.patch(`/admin/teacher-leads/${id}/unlock`);
      setTeacherLeads(ls => ls.map(l => l._id === id ? { ...l, ...data.lead } : l));
    } catch { alert("Failed to unlock lead. Try again."); }
  };

  const tabs = ["overview","teachers","students","payments","inquiries","teacher-leads","analytics","settings"];

  if (!user || user.role !== "admin") return (
    <div style={{ minHeight:"100vh", background:"#0a0a0a", display:"flex", alignItems:"center", justifyContent:"center", color:"#ef4444", fontSize:16, fontWeight:700 }}>
      Access denied.
    </div>
  );

  if (loading) return (
    <div style={{ textAlign:"center", padding:60, color:"#475569", background:"#0a0a0a", minHeight:"100vh" }}>
      <div style={{ fontSize:32, marginBottom:12 }}>⏳</div>
      Loading admin data...
    </div>
  );

  return (
    <div style={{ background:"#0a0a0a", minHeight:"100vh", color:"#f1f5f9" }}>
      <Helmet>
        <title>Admin Panel | PadhaiPass</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <style>{`
        .ap-grid3 { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin-bottom:16px; }
        .ap-grid5 { display:grid; grid-template-columns:repeat(5,1fr); gap:8px; margin-bottom:10px; }
        .ap-settings { max-width:480px; }
        .ap-tabs { display:flex; background:#111111; border-bottom:1px solid #1e293b; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
        .ap-tabs::-webkit-scrollbar { display:none; }
        .ap-tab-item { flex-shrink:0; padding:13px 14px; text-align:center; font-size:12px; cursor:pointer; text-transform:capitalize; white-space:nowrap; transition:color .2s; }
        .ap-teacher-top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:10px; }
        .ap-teacher-badges { display:flex; gap:6px; flex-wrap:wrap; justify-content:flex-end; }
        @media(max-width:520px){
          .ap-grid3 { grid-template-columns:repeat(2,1fr); }
          .ap-grid5 { grid-template-columns:repeat(3,1fr); }
          .ap-settings { max-width:100%; }
          .ap-payment-top { flex-direction:column; gap:6px; }
          .ap-payment-amount { text-align:left !important; }
          .ap-teacher-top { flex-direction:column; gap:8px; }
          .ap-teacher-badges { justify-content:flex-start; }
          .ap-header-inner { flex-wrap:wrap; gap:8px; }
        }
      `}</style>

      {/* Header */}
      <div style={{ background:"#111111", padding:"14px 16px", borderBottom:"1px solid #1e293b", display:"flex", justifyContent:"space-between", alignItems:"center" }} className="ap-header-inner">
        <div>
          <div style={{ fontSize:20, fontWeight:800 }}>
            <span style={{ color:"#fff" }}>Padhai</span>
            <span style={{ color:"#f97316" }}>Pass</span>
          </div>
          <div style={{ fontSize:12, color:"#475569", marginTop:2 }}>Admin Control Panel</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {(stats.newLeads||0) > 0 && (
            <span style={{ background:"#f9731622", color:"#f97316", fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, border:"1px solid #f9731644" }}>
              🔔 {stats.newLeads} new leads
            </span>
          )}
          <Badge label="Admin" color="#f97316" />
        </div>
      </div>

      {/* Tabs */}
      <div className="ap-tabs">
        {tabs.map(t => (
          <div key={t} className="ap-tab-item" onClick={() => setTab(t)}
            style={{ fontWeight:tab===t?700:400, color:tab===t?"#f97316":"#475569", borderBottom:`2px solid ${tab===t?"#f97316":"transparent"}` }}
          >
            {t === "inquiries"
              ? <>Inquiries {(stats.newLeads||0)>0 && <span style={{ background:"#f97316", color:"#fff", fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:10, marginLeft:4 }}>{stats.newLeads}</span>}</>
              : t === "teacher-leads"
              ? <>Teacher Leads {(stats.pendingTeacherLeads||0)>0 && <span style={{ background:"#f59e0b", color:"#fff", fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:10, marginLeft:4 }}>{stats.pendingTeacherLeads}</span>}</>
              : t.charAt(0).toUpperCase() + t.slice(1)
            }
          </div>
        ))}
      </div>

      <div style={{ padding:"16px" }}>

        {/* ── OVERVIEW TAB ── */}
        {tab === "overview" && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:10 }}>
              <StatCard value={stats.totalTeachers||0}    label="Total Teachers"   color="#f97316" />
              <StatCard value={stats.listedTeachers||0}   label="Listed in Search" color="#22c55e" sub="free listing" />
              <StatCard value={stats.totalStudents||0}    label="Students"         color="#0891b2" />
              <StatCard value={"₹"+(stats.totalRevenue||0)} label="Total Revenue"  color="#f97316" sub="listings + connections" />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:10 }}>
              <StatCard value={stats.blockedTeachers||0}      label="Blocked"          color="#ef4444" />
              <StatCard value={stats.unlistedTeachers||0}  label="Not Listed"       color="#f59e0b" sub="Profile incomplete" />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:10 }}>
              <StatCard value={stats.totalConnections||0}       label="Connections"         color="#22c55e" sub="Student paid ₹29 → got teacher WhatsApp" />
              <StatCard value={"₹"+(stats.contactRevenue||0)}  label="Connection Revenue"  color="#0891b2" sub="Students paying ₹29 each" />
            </div>
            <div style={{ marginBottom:10 }}>
              <StatCard value={stats.pendingTeacherLeads||0} label="Demo Leads (Pending)" color="#f59e0b" sub="Teachers awaiting relist payment" />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:10 }}>
              <StatCard value={stats.totalLeads||0}   label="General Inquiries" color="#f97316" sub="Students who couldn't find a tutor" />
              <StatCard value={stats.newLeads||0}     label="New Inquiries"     color="#22c55e" sub="Not yet contacted" />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:16 }}>
              <StatCard value={stats.totalProfileViews||0} label="Profile Views"   color="#8b5cf6" sub="All teachers combined" />
              <StatCard value={stats.pendingReviews||0}    label="Pending Reviews" color="#0891b2" sub="Awaiting teacher approval" />
            </div>

            {/* Recent payments */}
            <div style={{ fontWeight:700, fontSize:13, marginBottom:10, color:"#64748b", textTransform:"uppercase", letterSpacing:1 }}>Recent Payments</div>
            {payments.slice(0,5).map(p => (
              <div key={p._id} style={{ background:"#111111", borderRadius:10, border:"1px solid #1e293b", padding:"12px 14px", marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{p.payer_name}</div>
                  <div style={{ fontSize:11, color:"#475569", marginTop:2 }}>
                    {p.type === "listing" ? "Lead unlock (teacher)" : "Contact unlock (student)"}
                    {p.receiver_name ? ` → ${p.receiver_name}` : ""}
                  </div>
                  <div style={{ fontSize:11, color:"#334155", marginTop:1 }}>{new Date(p.createdAt).toLocaleString("en-IN")}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontWeight:700, color:"#22c55e" }}>₹{p.amount} ✓</div>
                </div>
              </div>
            ))}
            {payments.length === 0 && (
              <div style={{ textAlign:"center", padding:20, color:"#334155", fontSize:13 }}>No payments yet</div>
            )}

            {/* Recent leads */}
            {leads.length > 0 && (
              <>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:10, marginTop:16, color:"#64748b", textTransform:"uppercase", letterSpacing:1 }}>Recent Leads</div>
                {leads.slice(0,3).map(l => (
                  <div key={l._id} style={{ background:"#111111", borderRadius:10, border:"1px solid #f9731622", padding:"12px 14px", marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{l.name}</div>
                      <div style={{ fontSize:11, color:"#f97316", marginTop:2 }}>📞 {l.phone}</div>
                      <div style={{ fontSize:11, color:"#334155", marginTop:1 }}>{l.class||"—"} · {l.locality||l.city||"—"}</div>
                    </div>
                    <Badge label={statusLabel[l.status]||"New"} color={statusColor[l.status]||"#f97316"} />
                  </div>
                ))}
                <button onClick={() => setTab("inquiries")}
                  style={{ width:"100%", padding:"9px", background:"transparent", color:"#f97316", border:"1px solid #f9731633", borderRadius:8, fontSize:12, cursor:"pointer", fontWeight:600, marginTop:4 }}>
                  View All Leads →
                </button>
              </>
            )}
          </>
        )}

        {/* ── TEACHERS TAB ── */}
        {tab === "teachers" && (
          <>
            <div style={{ display:"flex", gap:8, marginBottom:14, alignItems:"center" }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email or city..."
                style={{ flex:1, padding:"10px 14px", borderRadius:8, border:"1px solid #1e293b", background:"#111111", color:"#f1f5f9", fontSize:13, outline:"none", boxSizing:"border-box" }}
              />
              <button onClick={refreshTeachers} disabled={tabLoading}
                style={{ padding:"10px 14px", borderRadius:8, border:"1px solid #1e293b", background:"#111111", color: tabLoading ? "#334155" : "#f97316", fontSize:13, cursor: tabLoading ? "default" : "pointer", fontWeight:600, whiteSpace:"nowrap", flexShrink:0 }}>
                {tabLoading ? "..." : "↺ Refresh"}
              </button>
            </div>
            {tabLoading && !teachers.length ? (
              <div style={{ textAlign:"center", padding:40, color:"#475569" }}>Loading teachers...</div>
            ) : (
            <>
            <div style={{ fontSize:12, color:"#475569", marginBottom:10 }}>{filteredTeachers.length} teachers found</div>

            {filteredTeachers.map(t => (
              <div key={t._id} style={{ background:"#111111", borderRadius:14, border:"1px solid #1e293b", padding:16, marginBottom:12, opacity:t.blocked?0.6:1 }}>
                <div className="ap-teacher-top">
                  <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                    {t.photo_url ? (
                      <img src={t.photo_url} alt={t.name}
                        style={{ width:44, height:44, borderRadius:10, objectFit:"cover", border:"1px solid #1e293b", flexShrink:0 }}
                        onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
                    ) : (
                      <div style={{ width:44, height:44, borderRadius:10, background:"linear-gradient(135deg,#f97316,#ea580c)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:15, flexShrink:0 }}>
                        {(t.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div onClick={() => nav("/admin/teacher/" + t._id)}
                        style={{ fontWeight:700, fontSize:14, cursor:"pointer", color:"#f97316", textDecoration:"underline" }}
                      >{t.name}</div>
                      <div style={{ fontSize:11, color:"#475569" }}>{t.email}</div>
                      <div style={{ fontSize:11, color:"#334155" }}>{t.area}{t.city?", "+t.city:""}</div>
                    </div>
                  </div>
                  <div className="ap-teacher-badges">
                    <Badge label={t.listing_active?"Listed":"Not Listed"} color={t.listing_active?"#22c55e":"#f59e0b"} />
                    {t.demo_awaiting_payment && <Badge label="⏳ Demo — Awaiting Fee" color="#f97316" />}
                    <Badge label={t.available?"Active":"Busy"} color={t.available?"#0891b2":"#64748b"} />
                    {t.blocked && <Badge label="Blocked" color="#ef4444" />}
                    {t.user?.referral_count > 0 && <Badge label={`🔗 ${t.user.referral_count}`} color="#a78bfa" />}
                  </div>
                </div>

                <div className="ap-grid5">
                  {[
                    ["Rating",   avg(t.ratings)+"★"],
                    ["Fee/mo",   t.fees_per_hour?"₹"+t.fees_per_hour+"/mo":"—"],
                    ["Connects", t.total_connections||0],
                    ["Leads",    (t.pending_leads||0)+"↑ / "+(t.unlocked_leads||0)+"✓"],
                    ["Views",    t.profile_views||0],
                  ].map(([l,v]) => (
                    <div key={l} style={{ background:"#0a0a0a", borderRadius:6, padding:"6px 8px", textAlign:"center" }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"#f1f5f9" }}>{v}</div>
                      <div style={{ fontSize:10, color:"#334155", marginTop:1 }}>{l}</div>
                    </div>
                  ))}
                </div>

                {t.subjects?.length > 0 && (
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
                    {t.subjects.map(s => (
                      <span key={s} style={{ background:"#f9731615", color:"#f97316", fontSize:10, padding:"2px 8px", borderRadius:6 }}>{s}</span>
                    ))}
                  </div>
                )}

                {t.listing_cf_order_id && (
                  <div style={{ background:"#0a0a0a", borderRadius:6, padding:"8px 10px", marginBottom:10, fontSize:11 }}>
                    <span style={{ color:"#22c55e", fontWeight:600 }}>Order ID: </span>
                    <span style={{ color:"#475569", fontFamily:"monospace" }}>{t.listing_cf_order_id}</span>
                  </div>
                )}

                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {t.demo_awaiting_payment ? (
                    <button onClick={() => relistFree(t._id)}
                      style={{ padding:"6px 14px", background:"#f9731622", color:"#f97316", border:"1px solid #f9731644", borderRadius:6, fontSize:12, cursor:"pointer", fontWeight:600 }}>
                      🔄 Relist for Free
                    </button>
                  ) : !t.listing_active ? (
                    <button onClick={() => activateListing(t._id)}
                      style={{ padding:"6px 14px", background:"#22c55e22", color:"#22c55e", border:"1px solid #22c55e44", borderRadius:6, fontSize:12, cursor:"pointer", fontWeight:600 }}>
                      ✓ Activate Listing
                    </button>
                  ) : (
                    <button onClick={() => deactivateListing(t._id)}
                      style={{ padding:"6px 14px", background:"#f59e0b22", color:"#f59e0b", border:"1px solid #f59e0b44", borderRadius:6, fontSize:12, cursor:"pointer", fontWeight:600 }}>
                      Deactivate Listing
                    </button>
                  )}
                  <button onClick={() => verify(t._id)}
                    style={{ padding:"5px 12px", background:t.verified?"#0ea5e9":"#0c2a3a", color:"#fff", border:"1.5px solid #0ea5e9", borderRadius:6, fontSize:12, cursor:"pointer", fontWeight:700, whiteSpace:"nowrap" }}>
                    {t.verified ? "✓ Verified" : "Mark Verified"}
                  </button>
                  <button onClick={() => block(t._id, t.blocked)}
                    style={{ padding:"6px 14px", background:t.blocked?"#22c55e22":"#ef444422", color:t.blocked?"#22c55e":"#ef4444", border:`1px solid ${t.blocked?"#22c55e44":"#ef444444"}`, borderRadius:6, fontSize:12, cursor:"pointer", fontWeight:600 }}>
                    {t.blocked ? "Unblock" : "Block"}
                  </button>
                </div>

              </div>
            ))}
            </>
            )}
          </>
        )}

        {/* ── STUDENTS TAB ── */}
        {tab === "students" && (
          <>
            {tabLoading && !students.length ? (
              <div style={{ textAlign:"center", padding:40, color:"#475569" }}>Loading students...</div>
            ) : (<>
            <div style={{ fontSize:13, color:"#475569", marginBottom:14 }}>
              {students.length} students · manage contact credits
            </div>
            {students.length === 0 ? (
              <div style={{ textAlign:"center", padding:40, color:"#475569" }}>No students yet</div>
            ) : students.map(s => (
              <div key={s._id} style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:"14px 16px", marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:"#f1f5f9" }}>{s.name}</div>
                  <div style={{ fontSize:12, color:"#475569", marginTop:2 }}>{s.email}</div>
                  {s.phone && (
                    <a href={"tel:+91"+s.phone.replace(/\D/g,"")}
                      style={{ fontSize:11, color:"#0891b2", marginTop:3, display:"block", textDecoration:"none" }}>
                      📞 {s.phone}
                    </a>
                  )}
                  <div style={{ fontSize:11, color:"#334155", marginTop:4 }}>
                    Joined {new Date(s.createdAt).toLocaleDateString("en-IN")}
                    {s.lastUnlockAt && ` · Last unlock ${new Date(s.lastUnlockAt).toLocaleDateString("en-IN")}`}
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:13, color: s.credits > 0 ? "#22c55e" : "#ef4444", fontWeight:700 }}>
                    {s.credits ?? 0} credits
                  </span>
                  <button onClick={() => adjustCredits(s._id, (s.credits ?? 0) + 3)}
                    style={{ padding:"6px 12px", background:"#22c55e22", color:"#22c55e", border:"1px solid #22c55e44", borderRadius:8, fontSize:12, cursor:"pointer", fontWeight:600 }}>
                    +3
                  </button>
                  <button onClick={() => adjustCredits(s._id, 0)}
                    style={{ padding:"6px 12px", background:"#ef444415", color:"#ef4444", border:"1px solid #ef444430", borderRadius:8, fontSize:12, cursor:"pointer", fontWeight:600 }}>
                    Reset
                  </button>
                </div>
              </div>
            ))}
            </>)}
          </>
        )}


        {/* ── PAYMENTS TAB ── */}
        {tab === "payments" && (
          <>
            <div className="ap-grid3">
              <StatCard
                value={payments.filter(p=>p.type==="contact_unlock").length}
                label="Credit Packs (₹29)" color="#22c55e"
                sub={"₹"+(payments.filter(p=>p.type==="contact_unlock").reduce((s,p)=>s+p.amount,0)+" earned")}
              />
              <StatCard
                value={payments.filter(p=>p.type==="listing_fee").length}
                label="Relist Fees" color="#a78bfa"
                sub={"₹"+(payments.filter(p=>p.type==="listing_fee").reduce((s,p)=>s+p.amount,0)+" earned")}
              />
            </div>
            <div style={{ textAlign:"right", fontSize:13, fontWeight:700, color:"#22c55e", marginBottom:12 }}>
              Total Revenue: ₹{payments.reduce((s,p)=>s+p.amount,0)}
            </div>

            {payments.map(p => (
              <div key={p._id} style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:"14px 16px", marginBottom:10 }}>
                {/* Top row */}
                <div className="ap-payment-top" style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
                      <span style={{ fontWeight:700, fontSize:14 }}>{p.payer_name}</span>
                      <Badge
                        label={p.type==="listing_fee" ? `₹${p.amount} Relist Fee` : "₹29 Credits"}
                        color={p.type==="listing_fee" ? "#a78bfa" : "#22c55e"}
                      />
                    </div>
                    <div style={{ fontSize:11, color:"#475569" }}>{p.payer_email}</div>
                    {p.payer_phone && <div style={{ fontSize:11, color:"#475569" }}>{p.payer_phone}</div>}
                  </div>
                  <div className="ap-payment-amount" style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontWeight:800, fontSize:16, color:"#22c55e" }}>₹{p.amount}</div>
                    <div style={{ fontSize:10, color:"#22c55e", marginTop:1 }}>✓ Paid</div>
                  </div>
                </div>

                {/* Who was unlocked */}
                {p.type === "contact_unlock" && p.receiver_name && (
                  <div style={{ fontSize:12, color:"#64748b", marginBottom:6 }}>
                    Unlocked: <span style={{ color:"#f1f5f9" }}>{p.receiver_name}</span>
                  </div>
                )}

                {/* Date */}
                <div style={{ fontSize:11, color:"#334155", marginBottom:8 }}>
                  {new Date(p.createdAt).toLocaleString("en-IN", { dateStyle:"medium", timeStyle:"short" })}
                </div>

                {/* Cashfree order ID */}
                <div style={{ background:"#0a0a0a", borderRadius:6, padding:"8px 10px" }}>
                  <div style={{ fontSize:11, marginBottom:4 }}>
                    <span style={{ color:"#475569" }}>Cashfree Order ID: </span>
                    <span style={{ color:"#94a3b8", fontFamily:"monospace", wordBreak:"break-all" }}>
                      {p.cashfree_order_id || "—"}
                    </span>
                  </div>
                  {p.cashfree_order_id && (
                    <a
                      href={"https://merchant.cashfree.com/merchant/pg#/orders/"+p.cashfree_order_id}
                      target="_blank" rel="noreferrer"
                      style={{ fontSize:11, color:"#f97316", textDecoration:"none" }}
                    >
                      View on Cashfree →
                    </a>
                  )}
                </div>
              </div>
            ))}

            {payments.length === 0 && (
              <div style={{ textAlign:"center", padding:40, color:"#334155" }}>No payment records yet.</div>
            )}
          </>
        )}

        {/* ── INQUIRIES TAB (students who couldn't find a tutor) ── */}
        {tab === "inquiries" && (
          <>
            <div className="ap-grid3">
              <StatCard value={leads.filter(l=>l.status==="new").length}       label="New"       color="#f97316" />
              <StatCard value={leads.filter(l=>l.status==="contacted").length} label="Contacted" color="#0891b2" />
              <StatCard value={leads.filter(l=>l.status==="converted").length} label="Converted" color="#22c55e" />
            </div>

            {leads.length === 0 ? (
              <div style={{ textAlign:"center", padding:"48px 20px", color:"#334155" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
                <div style={{ fontSize:15, color:"#475569" }}>No leads yet</div>
                <div style={{ fontSize:13, color:"#334155", marginTop:6 }}>When students can't find tutors they'll appear here</div>
              </div>
            ) : (
              leads.map(l => (
                <div key={l._id} style={{ background:"#111111", borderRadius:14, border:"1px solid #1e293b", padding:16, marginBottom:12 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:15, color:"#f1f5f9" }}>{l.name}</div>
                      <a href={"tel:+91"+l.phone.replace(/\D/g,"")}
                        style={{ fontSize:13, color:"#f97316", fontWeight:600, textDecoration:"none", display:"inline-flex", alignItems:"center", gap:4, marginTop:4 }}>
                        📞 {l.phone}
                      </a>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                      <Badge label={statusLabel[l.status]||"New"} color={statusColor[l.status]||"#f97316"} />
                      <div style={{ fontSize:10, color:"#334155" }}>
                        {new Date(l.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                      </div>
                    </div>
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
                    {[
                      ["📚 Class",    l.class    || "—"],
                      ["📍 Locality", l.locality || "—"],
                      ["🏙️ City",     l.city     || "—"],
                      ["📖 Subjects", l.subjects?.join(", ") || "—"],
                    ].map(([label, value]) => (
                      <div key={label} style={{ background:"#0a0a0a", borderRadius:8, padding:"8px 10px" }}>
                        <div style={{ fontSize:10, color:"#475569", marginBottom:3 }}>{label}</div>
                        <div style={{ fontSize:13, fontWeight:600, color:"#f1f5f9" }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Status buttons */}
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    {["new","contacted","converted"].map(s => (
                      <button key={s} onClick={() => updateLeadStatus(l._id, s)}
                        style={{ padding:"6px 14px", borderRadius:6, fontSize:12, cursor:"pointer", fontWeight:600, transition:"all .2s",
                          background: l.status===s ? statusColor[s]+"22" : "transparent",
                          color: l.status===s ? statusColor[s] : "#475569",
                          border: `1px solid ${l.status===s ? statusColor[s]+"44" : "#1e293b"}`
                        }}>
                        {l.status===s ? "✓ " : ""}{statusLabel[s]}
                      </button>
                    ))}
                    <a href={"https://wa.me/91"+l.phone.replace(/\D/g,"")+"?text="+encodeURIComponent("Hi "+l.name+", I am from PadhaiPass. We found a tutor for you for "+( l.class||"your class")+". Are you still looking?")}
                      target="_blank" rel="noreferrer"
                      style={{ padding:"6px 14px", background:"#25D366", color:"#fff", border:"none", borderRadius:6, fontSize:12, cursor:"pointer", textDecoration:"none", fontWeight:600, display:"inline-flex", alignItems:"center", gap:5 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </a>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* ── TEACHER LEADS TAB ── */}
        {tab === "teacher-leads" && (
          <>
            {tabLoading && !teacherLeads.length ? (
              <div style={{ textAlign:"center", padding:40, color:"#475569" }}>Loading leads...</div>
            ) : (<>
            <div className="ap-grid3" style={{ marginBottom:14 }}>
              <StatCard value={teacherLeads.filter(l=>l.status==="pending").length}  label="Pending"  color="#f59e0b" sub="demo confirmed" />
              <StatCard value={teacherLeads.filter(l=>l.status==="unlocked").length} label="Admin Unlocked" color="#22c55e" sub="force-unlocked by admin" />
            </div>

            {teacherLeads.length === 0 ? (
              <div style={{ textAlign:"center", padding:"48px 20px", color:"#334155" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
                <div style={{ fontSize:15, color:"#475569" }}>No teacher leads yet</div>
                <div style={{ fontSize:13, color:"#334155", marginTop:6 }}>Appears when students send interest to free-listed teachers</div>
              </div>
            ) : teacherLeads.map(l => (
              <div key={l._id} style={{ background:"#111111", borderRadius:14, border:`1px solid ${l.status==="unlocked"?"#22c55e33":"#1e293b"}`, padding:16, marginBottom:10 }}>

                {/* Status badge + date */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <Badge
                    label={l.status === "unlocked" ? "✓ Unlocked" : "⏳ Demo Pending"}
                    color={l.status === "unlocked" ? "#22c55e" : "#f59e0b"}
                  />
                  <div style={{ fontSize:11, color:"#334155" }}>
                    {new Date(l.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                  </div>
                </div>

                {/* Student → Teacher flow */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr", gap:10, alignItems:"center", marginBottom:12 }}>
                  {/* Student */}
                  <div style={{ background:"#0a0a0a", borderRadius:10, padding:"10px 12px", border:"1px solid #1e293b" }}>
                    <div style={{ fontSize:10, color:"#475569", marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Student</div>
                    <div style={{ fontWeight:700, fontSize:13, color:"#f1f5f9", marginBottom:3 }}>{l.student_name || l.student?.name || "—"}</div>
                    <div style={{ fontSize:11, color:"#64748b" }}>{l.student?.email || l.student_email || "—"}</div>
                    {(l.student?.phone || l.student_phone) && (
                      <a href={"tel:+91"+(l.student?.phone||l.student_phone).replace(/\D/g,"")}
                        style={{ fontSize:11, color:"#0891b2", textDecoration:"none", display:"block", marginTop:2 }}>
                        📞 {l.student?.phone || l.student_phone}
                      </a>
                    )}
                    {l.student_class && <div style={{ fontSize:11, color:"#f97316", marginTop:4 }}>📚 {l.student_class}</div>}
                    {l.student_area  && <div style={{ fontSize:11, color:"#64748b" }}>📍 {l.student_area}</div>}
                  </div>

                  {/* Arrow */}
                  <div style={{ textAlign:"center", color:"#f97316", fontSize:18, fontWeight:700 }}>→</div>

                  {/* Teacher */}
                  <div style={{ background:"#0a0a0a", borderRadius:10, padding:"10px 12px", border:"1px solid #1e293b" }}>
                    <div style={{ fontSize:10, color:"#475569", marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Teacher</div>
                    <div style={{ fontWeight:700, fontSize:13, color:"#f97316", marginBottom:3 }}>{l.teacher?.name || "—"}</div>
                    <div style={{ fontSize:11, color:"#64748b" }}>{l.teacher?.area}{l.teacher?.city ? ", "+l.teacher.city : ""}</div>
                    {l.teacher?.email && <div style={{ fontSize:11, color:"#475569", marginTop:2 }}>{l.teacher.email}</div>}
                    {l.wa_link && (
                      <a href={l.wa_link} target="_blank" rel="noreferrer"
                        style={{ fontSize:11, color:"#25D366", textDecoration:"none", display:"block", marginTop:4, fontWeight:600 }}>
                        WhatsApp →
                      </a>
                    )}
                  </div>
                </div>

                {/* Admin actions */}
                {l.status === "pending" && (
                  <button onClick={() => forceUnlockLead(l._id)}
                    style={{ marginBottom:8, padding:"6px 14px", background:"#22c55e22", color:"#22c55e", border:"1px solid #22c55e44", borderRadius:6, fontSize:12, cursor:"pointer", fontWeight:600 }}>
                    🔓 Force Unlock for Free
                  </button>
                )}


              </div>
            ))}
            </>)}
          </>
        )}

        {/* ── ANALYTICS TAB ── */}
        {tab === "analytics" && (
          <>
            <div style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:16, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#f97316", marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>Most Viewed Teachers</div>
              {[...teachers].sort((a,b)=>(b.profile_views||0)-(a.profile_views||0)).slice(0,5).map((t,i) => (
                <div key={t._id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px solid #1e293b" }}>
                  <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                    <div style={{ width:24, height:24, borderRadius:"50%", background:"#f97316", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:11, fontWeight:700 }}>{i+1}</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{t.name}</div>
                      <div style={{ fontSize:11, color:"#475569" }}>{t.city||"—"}</div>
                    </div>
                  </div>
                  <div style={{ color:"#f97316", fontWeight:700, fontSize:13 }}>{t.profile_views||0} views</div>
                </div>
              ))}
            </div>

            <div style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:16, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#f97316", marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>Top Rated Teachers</div>
              {[...teachers].filter(t=>t.ratings?.length>0).sort((a,b)=>avg(b.ratings)-avg(a.ratings)).slice(0,5).map((t,i) => (
                <div key={t._id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px solid #1e293b" }}>
                  <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                    <div style={{ width:24, height:24, borderRadius:"50%", background:"#f97316", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:11, fontWeight:700 }}>{i+1}</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{t.name}</div>
                      <div style={{ fontSize:11, color:"#475569" }}>{t.subjects?.join(", ")}</div>
                    </div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ color:"#f59e0b", fontSize:13 }}>★ {avg(t.ratings)}</div>
                    <div style={{ fontSize:11, color:"#475569" }}>{t.ratings?.length} reviews</div>
                  </div>
                </div>
              ))}
              {teachers.filter(t=>t.ratings?.length>0).length===0 && <div style={{ color:"#334155", fontSize:13 }}>No ratings yet</div>}
            </div>

            <div style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:16, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#f97316", marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>Subject Demand</div>
              {(() => {
                const subjectCount = {};
                teachers.forEach(t => t.subjects?.forEach(s => { subjectCount[s]=(subjectCount[s]||0)+1; }));
                const sorted = Object.entries(subjectCount).sort((a,b)=>b[1]-a[1]);
                const max = sorted[0]?.[1]||1;
                return sorted.map(([s,c]) => (
                  <div key={s} style={{ marginBottom:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                      <span style={{ color:"#94a3b8" }}>{s}</span>
                      <span style={{ color:"#475569" }}>{c} teachers</span>
                    </div>
                    <div style={{ background:"#1e293b", borderRadius:4, height:6 }}>
                      <div style={{ background:"#f97316", borderRadius:4, height:6, width:`${(c/max)*100}%`, transition:"width .3s" }} />
                    </div>
                  </div>
                ));
              })()}
            </div>

            <div style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:16, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#f97316", marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>Revenue Breakdown</div>
              {[
                ["Credit pack revenue (₹29 × packs)", "₹"+(stats.contactRevenue||0),  "#0891b2"],
                ["Total revenue",                      "₹"+(stats.totalRevenue||0),   "#f97316"],
                ["Pending demo leads",                 stats.pendingTeacherLeads||0,  "#f59e0b"],
              ].map(([l,v,c]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #1e293b", fontSize:13 }}>
                  <span style={{ color:"#64748b" }}>{l}</span>
                  <span style={{ color:c, fontWeight:700 }}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:16, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#f97316", marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>City Distribution</div>
              {(() => {
                const cityCount = {};
                teachers.forEach(t => { if(t.city) cityCount[t.city]=(cityCount[t.city]||0)+1; });
                return Object.entries(cityCount).sort((a,b)=>b[1]-a[1]).map(([city,count]) => (
                  <div key={city} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid #1e293b", fontSize:13 }}>
                    <span style={{ color:"#64748b" }}>{city}</span>
                    <span style={{ color:"#f97316", fontWeight:700 }}>{count} teachers</span>
                  </div>
                ));
              })()}
            </div>

            <div style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:16 }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#f97316", marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>Lead Analytics</div>
              {[
                ["Total Leads",    leads.length,                                        "#f97316"],
                ["New",           leads.filter(l=>l.status==="new").length,             "#f97316"],
                ["Contacted",     leads.filter(l=>l.status==="contacted").length,       "#0891b2"],
                ["Converted",     leads.filter(l=>l.status==="converted").length,       "#22c55e"],
                ["Conversion %",  leads.length ? Math.round(leads.filter(l=>l.status==="converted").length/leads.length*100)+"%" : "0%", "#22c55e"],
              ].map(([l,v,c]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #1e293b", fontSize:13 }}>
                  <span style={{ color:"#64748b" }}>{l}</span>
                  <span style={{ color:c, fontWeight:700 }}>{v}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── SETTINGS TAB ── */}
        {tab === "settings" && (
          <div className="ap-settings">
            <div style={{ background:"#111111", borderRadius:14, border:"1px solid #1e293b", padding:20, marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:14, color:"#f97316", marginBottom:4 }}>Free Trial Controls</div>
              <div style={{ fontSize:12, color:"#475569", marginBottom:20 }}>
                Set how many free trials each teacher and student gets. Set to 0 to disable free trials.
              </div>

              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:12, color:"#64748b", display:"block", marginBottom:6 }}>
                  Free Listings per Teacher
                </label>
                <input
                  type="number" min="0" max="99"
                  value={settings.teacher_free_listings}
                  onChange={e => setSettings(s => ({ ...s, teacher_free_listings: Number(e.target.value) }))}
                  style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #1e293b", background:"#0a0a0a", color:"#f1f5f9", fontSize:14, outline:"none", boxSizing:"border-box" }}
                />
                <div style={{ fontSize:11, color:"#334155", marginTop:4 }}>
                  Number of free listings allowed per teacher (0 = always free)
                </div>
              </div>

              <div style={{ marginBottom:20 }}>
                <label style={{ fontSize:12, color:"#64748b", display:"block", marginBottom:6 }}>
                  Free Contact Unlocks per Student
                </label>
                <input
                  type="number" min="0" max="99"
                  value={settings.student_free_contacts}
                  onChange={e => setSettings(s => ({ ...s, student_free_contacts: Number(e.target.value) }))}
                  style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #1e293b", background:"#0a0a0a", color:"#f1f5f9", fontSize:14, outline:"none", boxSizing:"border-box" }}
                />
                <div style={{ fontSize:11, color:"#334155", marginTop:4 }}>
                  Each student can unlock this many teacher contacts for free before paying ₹29
                </div>
              </div>

              <div style={{ borderTop:"1px solid #1e293b", paddingTop:16, marginBottom:16, marginTop:4 }}>
                <div style={{ fontWeight:700, fontSize:13, color:"#f97316", marginBottom:14 }}>Pricing</div>

                <div style={{ marginBottom:14 }}>
                  <label style={{ fontSize:12, color:"#64748b", display:"block", marginBottom:6 }}>
                    Credit Pack Price (₹) — student pays this to buy credits
                  </label>
                  <input
                    type="number" min="1"
                    value={settings.credit_pack_price ?? 29}
                    onChange={e => setSettings(s => ({ ...s, credit_pack_price: Number(e.target.value) }))}
                    style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #1e293b", background:"#0a0a0a", color:"#f1f5f9", fontSize:14, outline:"none", boxSizing:"border-box" }}
                  />
                </div>

                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:12, color:"#64748b", display:"block", marginBottom:6 }}>
                    Credits per Pack — how many credits per purchase
                  </label>
                  <input
                    type="number" min="1" max="100"
                    value={settings.credits_per_pack ?? 3}
                    onChange={e => setSettings(s => ({ ...s, credits_per_pack: Number(e.target.value) }))}
                    style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #1e293b", background:"#0a0a0a", color:"#f1f5f9", fontSize:14, outline:"none", boxSizing:"border-box" }}
                  />
                  <div style={{ fontSize:11, color:"#334155", marginTop:4 }}>e.g. ₹{settings.credit_pack_price ?? 29} = {settings.credits_per_pack ?? 3} credits</div>
                </div>
              </div>

              <button onClick={saveSettings}
                style={{ width:"100%", padding:"12px", background:"#f97316", color:"#fff", border:"none", borderRadius:10, fontSize:14, fontWeight:700, cursor:"pointer", transition:"all .2s", boxShadow:"0 0 12px #f9731644" }}>
                {settingsSaved ? "✓ Saved!" : "Save Settings"}
              </button>

              {settingsSaveError && (
                <div style={{ marginTop:10, padding:"10px 14px", background:"#1a0a0a", border:"1px solid #ef444430", borderRadius:8, color:"#ef4444", fontSize:13 }}>
                  ⚠️ {settingsSaveError}
                </div>
              )}
            </div>

            <div style={{ background:"#111111", borderRadius:12, border:"1px solid #1e293b", padding:16 }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#64748b", marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>Current Settings</div>
              {[
                ["Teacher free listings", settings.teacher_free_listings === 0 ? "Always free (0 = no limit)" : `${settings.teacher_free_listings} free listing(s) per teacher`],
                ["Student free contacts", settings.student_free_contacts === 0 ? "Disabled (always paid)" : `${settings.student_free_contacts} free unlock(s) per student`],
                ["Credit pack",           `₹${settings.credit_pack_price ?? 29} = ${settings.credits_per_pack ?? 3} credits`],
              ].map(([l,v]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #1e293b", fontSize:13 }}>
                  <span style={{ color:"#64748b" }}>{l}</span>
                  <span style={{ color:"#f97316", fontWeight:600 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}